import { createSignal } from 'solid-js'

import type { ColorTheme } from './colorThemeStore'

type AccentPalette = {
   accentBg: string
   accentHover: string
   surface: string
   border: string
   text: string
   glow: string
}

const ACCENT_THEMES = {
   blue: {
      accentBg: '#38bdf8',
      accentHover: '#0ea5e9',
      surface: 'rgba(56,189,248,0.12)',
      border: 'rgba(56,189,248,0.4)',
      text: '#e0f2fe',
      glow: 'rgba(56,189,248,0.35)',
   },
   green: {
      accentBg: '#22c55e',
      accentHover: '#16a34a',
      surface: 'rgba(34,197,94,0.12)',
      border: 'rgba(34,197,94,0.4)',
      text: '#dcfce7',
      glow: 'rgba(34,197,94,0.35)',
   },
   orange: {
      accentBg: '#f97316',
      accentHover: '#ea580c',
      surface: 'rgba(249,115,22,0.12)',
      border: 'rgba(249,115,22,0.4)',
      text: '#ffedd5',
      glow: 'rgba(249,115,22,0.35)',
   },
   red: {
      accentBg: '#ef4444',
      accentHover: '#dc2626',
      surface: 'rgba(239,68,68,0.12)',
      border: 'rgba(239,68,68,0.4)',
      text: '#fee2e2',
      glow: 'rgba(239,68,68,0.35)',
   },
} as const satisfies Record<string, AccentPalette>

// Paper variants: deeper fills so white button text still reads, and dark
// accent text instead of the pale tints that vanish on a light surface.
const PAPER_ACCENT_THEMES: Record<AccentThemeKey, AccentPalette> = {
   blue: {
      accentBg: '#0369a1',
      accentHover: '#075985',
      surface: 'rgba(3,105,161,0.08)',
      border: 'rgba(3,105,161,0.35)',
      text: '#0c4a6e',
      glow: 'rgba(3,105,161,0.18)',
   },
   green: {
      accentBg: '#15803d',
      accentHover: '#166534',
      surface: 'rgba(21,128,61,0.08)',
      border: 'rgba(21,128,61,0.35)',
      text: '#14532d',
      glow: 'rgba(21,128,61,0.18)',
   },
   orange: {
      accentBg: '#c2410c',
      accentHover: '#9a3412',
      surface: 'rgba(194,65,12,0.08)',
      border: 'rgba(194,65,12,0.35)',
      text: '#7c2d12',
      glow: 'rgba(194,65,12,0.18)',
   },
   red: {
      accentBg: '#b91c1c',
      accentHover: '#991b1b',
      surface: 'rgba(185,28,28,0.08)',
      border: 'rgba(185,28,28,0.35)',
      text: '#7f1d1d',
      glow: 'rgba(185,28,28,0.18)',
   },
}

export type AccentThemeKey = keyof typeof ACCENT_THEMES

const [accentTheme, setAccentThemeSignal] = createSignal<AccentThemeKey>('blue')

export { accentTheme }

/**
 * Sets the active accent theme, defaulting to blue for unknown keys.
 *
 * @param themeKey - The accent theme key.
 */
export function setAccentTheme(themeKey: string) {
   const nextTheme =
      Object.prototype.hasOwnProperty.call(ACCENT_THEMES, themeKey) ?
         (themeKey as AccentThemeKey)
      :  'blue'
   setAccentThemeSignal(previousTheme =>
      previousTheme === nextTheme ? previousTheme : nextTheme,
   )
}

/**
 * Applies an accent theme's colors to the document root CSS variables.
 *
 * @param themeKey - The accent theme key.
 * @param colorTheme - The active color theme, which picks the palette variant.
 */
export function applyAccentThemeToDocument(
   themeKey: AccentThemeKey,
   colorTheme: ColorTheme = 'midnight',
) {
   if (typeof document === 'undefined') {
      return
   }

   const theme =
      colorTheme === 'paper' ? PAPER_ACCENT_THEMES[themeKey] : ACCENT_THEMES[themeKey]
   const rootStyle = document.documentElement.style
   rootStyle.setProperty('--accent-bg', theme.accentBg)
   rootStyle.setProperty('--accent-bg-hover', theme.accentHover)
   rootStyle.setProperty('--accent-surface', theme.surface)
   rootStyle.setProperty('--accent-border', theme.border)
   rootStyle.setProperty('--accent-text', theme.text)
   rootStyle.setProperty('--accent-glow', theme.glow)
}
